import React from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { formatCurrency, getCurrencySymbol } from '../../utils/currency';
import Button from '../../components/ui/Button';

export function emptyItem() {
  return {
    name: '',
    description: '',
    quantity: 1,
    unitPrice: 0,
    taxRate: 18,
  };
}

export function computeLineTotal(item, isGst = true) {
  const qty = Number(item.quantity) || 0;
  const price = Number(item.unitPrice) || 0;
  const tax = Number(item.taxRate) || 0;
  const subtotal = qty * price;
  const taxAmt = isGst ? subtotal * (tax / 100) : 0;
  return { subtotal, taxAmt, total: subtotal + taxAmt };
}

export function computeTotals(items, isGst = true) {
  return (items || []).reduce(
    (acc, it) => {
      const { subtotal, taxAmt, total } = computeLineTotal(it, isGst);
      acc.subtotal += subtotal;
      acc.tax += taxAmt;
      acc.total += total;
      return acc;
    },
    { subtotal: 0, tax: 0, total: 0 },
  );
}

export default function InvoiceItemsEditor({ items, onChange, errors = {}, isGst = true, currency = 'INR' }) {
  const setRow = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange(next);
  };

  const addRow = () => onChange([...(items || []), emptyItem()]);

  const removeRow = (idx) => {
    if (items.length <= 1) {
      onChange([emptyItem()]);
      return;
    }
    onChange(items.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Line items</span>
      </div>
      <div className="flex flex-col gap-2">
        {(items || []).map((item, idx) => {
          const { total } = computeLineTotal(item, isGst);
          const rowErr = errors[idx] || {};
          return (
            <div key={idx} className="border border-slate-200 rounded-md overflow-hidden bg-white">
              <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Item {idx + 1}</span>
                <div className="flex items-center gap-2.5">
                  <span className="text-[13px] font-medium text-slate-900">{formatCurrency(total, currency)}</span>
                  <button
                    type="button"
                    onClick={() => removeRow(idx)}
                    className="w-5 h-5 border-none bg-transparent cursor-pointer text-slate-400 rounded-md flex items-center justify-center hover:bg-red-50 hover:text-red-600 transition-colors"
                    aria-label="Remove item"
                  >
                    <FiTrash2 size={13} />
                  </button>
                </div>
              </div>
              <div className="p-3 flex flex-col gap-2.5">
                <div>
                  <label className="block text-[11px] text-slate-500 mb-1 tracking-wide">Item name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={item.name}
                    onChange={(e) => setRow(idx, { name: e.target.value })}
                    placeholder="e.g. Consultation fee"
                    className={`w-full h-[32px] border ${rowErr.name ? 'border-red-300' : 'border-slate-200'} rounded-md bg-white text-slate-900 text-[13px] px-2 focus:outline-none focus:border-blue-500`}
                  />
                  {rowErr.name && <span className="text-[10px] text-red-500">{rowErr.name}</span>}
                </div>
                <div>
                  <label className="block text-[11px] text-slate-500 mb-1 tracking-wide">Description <span className="text-[11px] text-slate-400 normal-case tracking-normal">(optional)</span></label>
                  <input
                    type="text"
                    value={item.description || ''}
                    onChange={(e) => setRow(idx, { description: e.target.value })}
                    placeholder="Short description"
                    className="w-full h-[32px] border border-slate-200 rounded-md bg-white text-slate-900 text-[13px] px-2 focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-[11px] text-slate-500 mb-1 tracking-wide">Units <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={item.quantity}
                      onChange={(e) => setRow(idx, { quantity: e.target.value })}
                      onFocus={(e) => {
                        const v = String(item.quantity);
                        if (v === '1' || v === '1.0' || v === '') {
                          e.target.select();
                          setRow(idx, { quantity: '' });
                        } else {
                          e.target.select();
                        }
                      }}
                      onBlur={() => {
                        if (item.quantity === '' || item.quantity === null || item.quantity === undefined) {
                          setRow(idx, { quantity: 1 });
                        }
                      }}
                      className={`w-full h-[32px] border ${rowErr.quantity ? 'border-red-300' : 'border-slate-200'} rounded-md bg-white text-slate-900 text-[13px] px-2 text-right focus:outline-none focus:border-blue-500`}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-500 mb-1 tracking-wide">Unit price ({getCurrencySymbol(currency)}) <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={item.unitPrice}
                      onChange={(e) => setRow(idx, { unitPrice: e.target.value })}
                      onFocus={(e) => {
                        const v = String(item.unitPrice);
                        if (v === '0' || v === '0.0' || v === '0.00' || v === '') {
                          e.target.select();
                          setRow(idx, { unitPrice: '' });
                        } else {
                          e.target.select();
                        }
                      }}
                      onBlur={() => {
                        if (item.unitPrice === '' || item.unitPrice === null || item.unitPrice === undefined) {
                          setRow(idx, { unitPrice: 0 });
                        }
                      }}
                      className={`w-full h-[32px] border ${rowErr.unitPrice ? 'border-red-300' : 'border-slate-200'} rounded-md bg-white text-slate-900 text-[13px] px-2 text-right focus:outline-none focus:border-blue-500`}
                    />
                  </div>
                  <div>
                    <label className={`block text-[11px] mb-1 tracking-wide ${!isGst ? 'text-slate-300' : 'text-slate-500'}`}>Tax %</label>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={isGst ? item.taxRate : 0}
                      disabled={!isGst}
                      onChange={(e) => setRow(idx, { taxRate: e.target.value === '' ? '' : Number(e.target.value) })}
                      className="w-full h-[32px] border border-slate-200 rounded-md bg-white text-slate-900 text-[13px] px-2 text-right focus:outline-none focus:border-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="h-2"></div>
      <button
        type="button"
        onClick={addRow}
        className="flex items-center justify-center gap-1.5 text-[13px] text-slate-500 border border-dashed border-slate-300 rounded-md p-2 bg-transparent cursor-pointer w-full hover:bg-slate-50 hover:text-slate-900 hover:border-solid transition-all"
      >
        <FiPlus size={14} /> Add item
      </button>
    </div>
  );
}
